/**
 * «Финансы → Эквайринг»: выплаты CloudPayments по дням — сколько продано,
 * сколько удержано, сколько пришло на счёт и каким зачислением.
 *
 * Зачисление в выписке нетто; реестр возвращает P&L выручку брутто и
 * комиссию расходом. Выплата без зачисления — выписка счёта ещё не загружена.
 * Спека: `docs/specs/2026-09-29-cloudpayments-design.md`.
 */
import { RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { Combobox } from "@/components/Combobox";
import { DateRangePicker, withPeriod } from "@/components/DateRangePicker";
import { NoAccess } from "@/components/NoAccess";
import { PNL, STATEMENTS, useCan } from "@/lib/access";
import { loadPayouts, loadSync, type PayoutPage, startSync, type SyncState } from "@/lib/acquiring";
import { money } from "@/lib/pnl";
import { type DirectoryRow, ENTREPRENEURS, loadDirectory } from "@/lib/statements";

const INPUT =
  "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] outline-none focus:border-border-strong";
const TH = "border-b border-border px-2.5 py-2 text-left";
const TD = "border-b border-border px-2.5 py-1.5";
const LIMIT = 200;

function day(iso: string | null): string {
  return iso ? iso.split("-").reverse().join(".") : "—";
}

function time(iso: string | null): string {
  return iso ? iso.replace("T", " ").slice(0, 16) : "—";
}

export function AcquiringPage() {
  return useCan(PNL) ? <AcquiringScreen /> : <NoAccess what="эквайрингу" />;
}

function AcquiringScreen() {
  const canSync = useCan(STATEMENTS);
  const [params, setParams] = useSearchParams();
  const [entrepreneurs, setEntrepreneurs] = useState<DirectoryRow[]>([]);
  const [page, setPage] = useState<PayoutPage | null>(null);
  const [failure, setFailure] = useState("");
  const [sync, setSync] = useState<SyncState | null>(null);
  const [since, setSince] = useState("");

  const filter = useMemo(
    () => ({
      entrepreneur_id: params.get("entrepreneur_id") ? Number(params.get("entrepreneur_id")) : undefined,
      date_from: params.get("date_from") ?? undefined,
      date_to: params.get("date_to") ?? undefined,
      unmatched: params.get("unmatched") === "1",
      limit: LIMIT,
      offset: Number(params.get("offset") ?? 0),
    }),
    [params],
  );

  const setFilter = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(params);
    if (!value) next.delete(key);
    else next.set(key, value);
    if (key !== "offset") next.delete("offset");
    setParams(next);
  };

  const setPeriod = (from: string, to: string) => {
    const next = withPeriod(params, from, to);
    next.delete("offset");
    setParams(next);
  };

  const reload = useCallback(() => {
    setPage(null);
    loadPayouts(filter)
      .then(setPage)
      .catch((error: Error) => setFailure(error.message));
  }, [filter]);

  useEffect(() => {
    loadDirectory(ENTREPRENEURS, true)
      .then(setEntrepreneurs)
      .catch((error: Error) => setFailure(error.message));
    loadSync()
      .then(setSync)
      .catch(() => undefined);
  }, []);

  useEffect(reload, [reload]);

  // Пока выгрузка идёт — спрашиваем итог; закончилась — перечитываем таблицу.
  const running = sync?.running ?? false;
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => {
      loadSync()
        .then((state) => {
          setSync(state);
          if (!state.running) reload();
        })
        .catch(() => undefined);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [running, reload]);

  const launch = () => {
    setFailure("");
    startSync(since || null)
      .then(setSync)
      .catch((error: Error) => setFailure(error.message));
  };

  const report = sync?.report ?? null;

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:flex-none">
      <div className="text-[12.5px] text-fg-muted">
        CloudPayments выплачивает на счёт нетто — за вычетом комиссии. Реестр по дням возвращает в P&L выручку брутто
        и комиссию расходом; выплата без зачисления значит, что выписка счёта за эти дни ещё не загружена в{" "}
        <Link to="/statements" className="underline">
          «Выписки»
        </Link>
        . СБП деньги зачисляет банк напрямую — такие строки справочные.
      </div>

      {canSync ? (
        <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-card)] border border-border px-3 py-2 text-[12.5px]">
          <button
            type="button"
            disabled={running}
            onClick={launch}
            className="flex h-8 items-center gap-2 rounded-[var(--radius-ui)] bg-fg px-3 text-[12.5px] font-medium text-bg disabled:opacity-50"
          >
            <RefreshCw size={13} className={running ? "animate-spin" : ""} />
            {running ? "Выгружаю из CloudPayments…" : "Обновить из CloudPayments"}
          </button>
          <label className="flex items-center gap-1.5 text-fg-muted">
            с месяца
            <input
              type="month"
              className={INPUT}
              value={since}
              disabled={running}
              onChange={(e) => setSince(e.target.value)}
              title="Пусто — прошлый и текущий месяц"
            />
          </label>
          <SyncSummary sync={sync} />
        </div>
      ) : null}
      {report?.error ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          Выгрузка не удалась: {report.error}
        </div>
      ) : null}
      {report && report.failed.length > 0 ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-warn/30 px-3 py-2 text-[12px] text-warn">
          Не загрузились: {report.failed.join("; ")}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        <Combobox
          className="max-w-[260px]"
          value={String(filter.entrepreneur_id ?? "")}
          onChange={(v) => setFilter("entrepreneur_id", v)}
          options={[{ value: "", label: "Все ИП" }, ...entrepreneurs.map((e) => ({ value: String(e.id), label: e.name }))]}
        />
        <DateRangePicker label="период" from={filter.date_from ?? ""} to={filter.date_to ?? ""} onChange={setPeriod} />
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={filter.unmatched}
            onChange={(e) => setFilter("unmatched", e.target.checked ? "1" : undefined)}
          />
          только без зачисления
        </label>
      </div>

      {failure ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {failure}
        </div>
      ) : null}

      {page === null ? (
        <div className="text-[12.5px] text-fg-muted">…</div>
      ) : page.total === 0 ? (
        <div className="text-[12.5px] text-fg-muted">
          Выплат нет. Реестры появляются после выгрузки из CloudPayments или загрузки файлов реестров в «Выписки».
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12.5px]">
            <span>
              операций <b className="tabular-nums">{page.sum.count}</b>
            </span>
            <span>
              продано <b className="tabular-nums">{money(page.sum.amount)}</b>
            </span>
            <span>
              комиссия <b className="tabular-nums">{money(page.sum.fee)}</b>
            </span>
            {Number(page.sum.held) ? (
              <span>
                удержания <b className="tabular-nums">{money(page.sum.held)}</b>
              </span>
            ) : null}
            <span>
              к выплате <b className="tabular-nums">{money(page.sum.payout)}</b>
            </span>
            <span>
              пришло на счёт <b className="tabular-nums">{money(page.sum.matched_payout)}</b>
            </span>
            {page.sum.unmatched > 0 ? (
              <span className="text-warn">
                без зачисления: <b className="tabular-nums">{page.sum.unmatched}</b>
              </span>
            ) : null}
          </div>
          <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
            <table className="w-full border-collapse text-[12.5px]">
              <thead>
                <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
                  <th className={TH}>день</th>
                  <th className={TH}>ИП</th>
                  <th className={TH}>шлюз</th>
                  <th className={`${TH} text-right`}>операций</th>
                  <th className={`${TH} text-right`}>продано</th>
                  <th className={`${TH} text-right`}>комиссия</th>
                  <th className={`${TH} text-right`}>удержания</th>
                  <th className={`${TH} text-right`}>к выплате</th>
                  <th className={TH}>зачисление</th>
                </tr>
              </thead>
              <tbody>
                {page.rows.map((p) => (
                  <tr key={p.id} data-annot={`реестр ${p.id}`} className="hover:bg-bg-hover">
                    <td className={`${TD} whitespace-nowrap tabular-nums`}>{day(p.date)}</td>
                    <td className={`${TD} text-fg-muted`}>{p.entrepreneur_name ?? "—"}</td>
                    <td className={`${TD} text-fg-muted`}>{p.gateway}</td>
                    <td className={`${TD} text-right tabular-nums`}>{p.count}</td>
                    <td className={`${TD} text-right tabular-nums`}>{money(p.amount)}</td>
                    <td className={`${TD} text-right tabular-nums text-fg-muted`}>{p.direct ? "—" : money(p.fee)}</td>
                    <td className={`${TD} text-right tabular-nums text-fg-muted`}>
                      {Number(p.held) ? money(p.held) : "—"}
                    </td>
                    <td className={`${TD} text-right tabular-nums`}>{p.direct ? "—" : money(p.payout)}</td>
                    <td className={`${TD} whitespace-nowrap`}>
                      {p.direct ? (
                        <span className="text-fg-faint">банк напрямую</span>
                      ) : p.operation_id ? (
                        <Link
                          to={`/pnl/operations?account_id=${p.account_id}&date_from=${p.operation_date}&date_to=${p.operation_date}`}
                          className="tabular-nums hover:underline"
                          title="Открыть операции счёта за этот день"
                        >
                          {day(p.operation_date)} · {p.account}
                        </Link>
                      ) : (
                        <span className="text-warn">нет зачисления</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {page.total > LIMIT ? (
            <div className="flex items-center gap-2 text-[12px] text-fg-muted">
              <button
                type="button"
                disabled={filter.offset === 0}
                onClick={() => setFilter("offset", String(Math.max(0, filter.offset - LIMIT)) || undefined)}
                className="rounded border border-border px-2 py-0.5 disabled:opacity-40"
              >
                ←
              </button>
              {filter.offset + 1}–{Math.min(filter.offset + LIMIT, page.total)} из {page.total}
              <button
                type="button"
                disabled={filter.offset + LIMIT >= page.total}
                onClick={() => setFilter("offset", String(filter.offset + LIMIT))}
                className="rounded border border-border px-2 py-0.5 disabled:opacity-40"
              >
                →
              </button>
            </div>
          ) : null}
        </>
      )}
    </main>
  );
}

function SyncSummary({ sync }: { sync: SyncState | null }) {
  const report = sync?.report;
  if (!report) return <span className="text-fg-muted">Выгрузок ещё не было с запуска сервиса.</span>;
  if (sync?.running) return <span className="text-fg-muted">идёт с {time(report.started)}, месяцы {report.months}…</span>;
  return (
    <span className="text-fg-muted">
      {time(report.finished)} · {report.months} · новых {report.new}, обновлено {report.updated}, без изменений{" "}
      {report.unchanged}, пустых {report.empty}
    </span>
  );
}
